import React, { ErrorInfo, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { AlertTriangle } from "lucide-react"
import { tokens } from "@/design-system/tokens"
import { Button } from "@/components/atoms/Button"
import { ErrorBoundary } from "./ErrorBoundary"
import { hasUnsavedWork } from "@/lib/unsavedWork"

interface RouteBoundaryProps {
  children: React.ReactNode
}

/**
 * Per-route error boundary. Renders an inline fallback inside the
 * surrounding layout (sidebar/header stay interactive) when the
 * wrapped page module crashes — covers React #31/#310 + lucide-as-child
 * + token-path crashes that previously blanked the whole app.
 *
 * Resets when the user navigates so going back to a working page
 * doesn't keep the error state.
 */
export function RouteBoundary({ children }: RouteBoundaryProps) {
  const location = useLocation()
  return (
    <ErrorBoundary key={location.pathname} fallback={(err, info) => <RouteErrorFallback error={err} errorInfo={info} />}>
      {children}
    </ErrorBoundary>
  )
}

// A lazy-loaded route chunk fails to load when a new version was deployed while
// this tab was open: the in-memory (old) index references chunk filenames that
// the freshly-deployed binary no longer embeds. The index is served no-store,
// so a full reload fetches the new index + correct chunk names and recovers.
// Auto-reload once (guarded against loops) so a deploy never strands an open tab.
function isChunkLoadError(error?: Error): boolean {
  const msg = String(error?.message || error || "")
  return /dynamically imported module|importing a module script|failed to fetch dynamically|chunkloaderror|error loading dynamically imported|unable to preload/i.test(msg)
}

function RouteErrorFallback({ error, errorInfo }: { error?: Error; errorInfo?: ErrorInfo }) {
  const chunkError = isChunkLoadError(error)
  // After a few auto-reloads fail to recover, STOP reloading and fall through to
  // the full fallback (with Reload + Go-home) so the user is never trapped on the
  // "Updating…" screen — e.g. if the chunk is genuinely missing, or several
  // deploys land back-to-back faster than a single reload can settle.
  const [exhausted, setExhausted] = useState(false)

  useEffect(() => {
    if (!chunkError) return
    // Never auto-reload over unsaved edits — the "Updating…" screen below keeps
    // the manual Reload button so the user can save first.
    if (hasUnsavedWork()) return
    const KEY = "tenon_chunk_reload"
    let st = { n: 0, at: 0 }
    try { st = JSON.parse(sessionStorage.getItem(KEY) || "") } catch { /* fresh */ }
    const now = Date.now()
    // A failure >30s after the last attempt is a fresh, unrelated one — reset.
    if (!st.at || now - st.at > 30000) st = { n: 0, at: 0 }
    if (st.n >= 4) { setExhausted(true); return }
    sessionStorage.setItem(KEY, JSON.stringify({ n: st.n + 1, at: now }))
    // Retry with a short backoff: 0s, then 1.5s, 3s, 4.5s. Keeps retrying through
    // a stale index / a brief deploy window until a stable bundle is served.
    const delay = st.n === 0 ? 0 : Math.min(st.n * 1500, 4500)
    const t = setTimeout(() => window.location.reload(), delay)
    return () => clearTimeout(t)
  }, [chunkError])

  if (chunkError && !exhausted) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "48px 24px", textAlign: "center", minHeight: "calc(100vh - 120px)",
        color: tokens.color.text.secondary,
      }}>
        <p style={{ fontSize: 15 }}>Updating to the latest version…</p>
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <Button variant="secondary" onClick={() => window.location.reload()}>Reload now</Button>
          <Button variant="primary" onClick={() => (window.location.href = "/")}>Go to home</Button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
        textAlign: "center",
        background: tokens.color.background.surface,
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <div
        style={{
          background: "#EF444422",
          color: "#EF4444",
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        <AlertTriangle size={36} />
      </div>
      <h2
        style={{
          fontSize: "20px",
          color: tokens.color.text.primary,
          marginBottom: "10px",
        }}
      >
        This page didn&apos;t load
      </h2>
      <p
        style={{
          color: tokens.color.text.secondary,
          marginBottom: "20px",
          maxWidth: "440px",
        }}
      >
        Something went wrong while rendering this view. Try reloading,
        or pick another page from the sidebar — the rest of Tenon is
        still working.
      </p>
      <div style={{ display: "flex", gap: "10px" }}>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          Reload page
        </Button>
        <Button variant="primary" onClick={() => (window.location.href = "/")}>
          Go to home
        </Button>
      </div>
      <p
        style={{
          marginTop: "16px",
          fontSize: "12px",
          color: tokens.color.text.muted,
        }}
      >
        Error reported automatically. Mention what you were doing if you contact support.
      </p>
      {error && (
        <details
          style={{
            marginTop: "18px",
            maxWidth: "680px",
            width: "100%",
            textAlign: "left",
            background: tokens.color.background.elevated,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "8px",
            padding: "10px 12px",
          }}
        >
          <summary style={{ cursor: "pointer", color: tokens.color.text.secondary, fontSize: 12, fontWeight: 600 }}>
            Error details (for support)
          </summary>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 11,
              lineHeight: 1.5,
              color: tokens.color.text.muted,
              marginTop: 8,
              maxHeight: 320,
              overflow: "auto",
            }}
          >
            {String(error.message || error)}
            {"\n\n"}
            {error.stack}
            {errorInfo?.componentStack ? "\n\nComponent stack:" + errorInfo.componentStack : ""}
          </pre>
        </details>
      )}
    </div>
  )
}
